// src/components/Public/PropertyListings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';

import { FaBed, FaBath, FaCar, FaMapMarkerAlt, FaRulerCombined, FaCompass, FaSearch, FaImages } from 'react-icons/fa';

function PropertyListings() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Add window resize listener for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check initial screen size
    checkScreenSize();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const fetchPublicProperties = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'properties'),
          where('status', '==', 'Available'),
          orderBy('is_featured', 'desc'),
          orderBy('date_status', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const fetchedProperties = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProperties(fetchedProperties);
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError("Failed to load properties. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchPublicProperties();
  }, []);

  const filteredProperties = properties.filter(property => {
    const query = searchQuery.toLowerCase();
    if (!query) return true; // If search query is empty, show all properties

    // Check if title, project_name, location, or display_id includes the query
    return (
      (property.title && property.title.toLowerCase().includes(query)) ||
      (property.project_name && property.project_name.toLowerCase().includes(query)) ||
      (property.location && property.location.toLowerCase().includes(query)) ||
      (property.display_id && String(property.display_id).includes(query))
    );
  });

  if (loading) return <p style={messageStyle}>Loading properties...</p>;
  if (error) return <p style={errorStyle}>{error}</p>;

  return (
    <div style={getListingsPageContainerStyle(isMobile)}>
      <div style={getSidebarStyle(isMobile)}>
        <h3 style={getSidebarTitleStyle(isMobile)}>Search</h3>

        <div style={searchContainerStyle}>
          <FaSearch style={getSearchIconStyle(isMobile)} />
          <input
            type="text"
            placeholder="ID, Title, Project, Location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={getSearchInputStyle(isMobile)}
          />
        </div>
      </div>

      <div style={getPropertyContentAreaStyle(isMobile)}>
        <h1 style={getPageTitleStyle(isMobile)}>Available Properties</h1>
        {filteredProperties.length === 0 ? (
          <p style={messageStyle}>No available properties found.</p>
        ) : (
          <div style={propertyListingsContainerStyle}>
            {filteredProperties.map(property => (
              <Link to={`/listing/${property.slug || property.id}`} key={property.id} style={getPropertyCardLinkStyle(isMobile)}>
                <div style={propertyCardStyle}>
                  <div style={propertyImageWrapperStyle}>
                    <img
                      src={property.images && property.images.length > 0 ? property.images[0] : 'placeholder_image_url'}
                      alt={property.title}
                      style={propertyCardImageStyle}
                    />
                  </div>
                  <div style={propertyCardContentStyle}>
                    <h2 style={propertyCardTitleStyle}>{property.title}</h2>
                    
                    {/* UPDATED ID AND PHOTO COUNT ROW */}
                    <div style={propertyCardIdRowStyle}>
                      {property.display_id && (
                        <span style={propertyCardIdStyle}>ID: {property.display_id}</span>
                      )}
                      {property.images && property.images.length > 0 && (
                        <span style={photoCountStyle}>
                          <FaImages style={photoIconStyle} />
                          {property.images.length}
                        </span>
                      )}
                    </div>
                    
                    {property.project_name && <h3 style={propertyCardSubTitleStyle}>{property.project_name}</h3>}

                    <div style={propertyInfoRowStyle}>
                      <p style={propertyLocationStyle}>
                        <FaMapMarkerAlt style={iconStyle} /> {property.location}
                      </p>
                    </div>

                    <div style={propertyInfoRowStyle}>
                      {property.listing_type && property.listing_type.length > 0 && (
                        <p style={propertyDetailItemStyle}>
                          <FaCompass style={iconStyle} /> {property.listing_type.map(type => type.charAt(0).toUpperCase() + type.slice(1)).join(', ')}
                        </p>
                      )}
                      {typeof property.built_up_area === 'number' && property.built_up_area > 0 && (
                        <p style={propertyDetailItemStyle}>
                          <FaRulerCombined style={iconStyle} /> {property.built_up_area.toLocaleString()} sqft (Built-up)
                        </p>
                      )}
                      {typeof property.land_area === 'number' && property.land_area > 0 &&
                      property.land_area !== property.built_up_area && (
                        <p style={propertyDetailItemStyle}>
                          <FaCompass style={iconStyle} /> {property.land_area.toLocaleString()} sqft (Land)
                        </p>
                      )}
                    </div>

                    <div style={propertyInfoRowStyle}>
                      {property.bedrooms && (
                        <p style={propertyDetailItemStyle}>
                          <FaBed style={iconStyle} /> {property.bedrooms} Beds
                        </p>
                      )}
                      {property.bathrooms && (
                        <p style={propertyDetailItemStyle}>
                          <FaBath style={iconStyle} /> {property.bathrooms} Baths
                        </p>
                      )}
                      {property.carparks && (
                        <p style={propertyDetailItemStyle}>
                          <FaCar style={iconStyle} /> {property.carparks} CP
                        </p>
                      )}
                    </div>

                    {/* UPDATED PRICE SECTION */}
                    {property.listing_type.includes('sale') && property.price_sale && (
                      <div style={propertyCardPriceContainerStyle}>
                        <span style={propertyCardPriceLabelStyle}>For Sale</span>
                        <span style={propertyCardPriceValueStyle}>RM {property.price_sale.toLocaleString()}</span>
                      </div>
                    )}
                    {property.listing_type.includes('rent') && property.price_rent && (
                      <div style={propertyCardPriceContainerStyle}>
                        <span style={propertyCardPriceLabelStyle}>For Rent</span>
                        <span style={propertyCardPriceValueStyle}>RM {property.price_rent.toLocaleString()} / month</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Responsive Style Functions ---

const getListingsPageContainerStyle = (isMobile) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: 'space-between',
  padding: isMobile ? '10px' : '20px',
  maxWidth: '1200px',
  margin: '0 auto',
});

const getSidebarStyle = (isMobile) => ({
  width: isMobile ? '100%' : '25%',
  padding: '15px',
  backgroundColor: '#f0f2f5',
  borderRadius: '8px',
  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  marginRight: isMobile ? '0' : '20px',
  marginBottom: isMobile ? '20px' : '0',
  alignSelf: 'flex-start',
});

const getSidebarTitleStyle = (isMobile) => ({
  fontSize: isMobile ? '1.5em' : '1.8em',
  color: '#333',
  marginBottom: '20px',
  borderBottom: '2px solid #007bff',
  paddingBottom: '10px',
});

const getPropertyContentAreaStyle = (isMobile) => ({
  width: isMobile ? '100%' : '75%',
  display: 'flex',
  flexDirection: 'column',
});

const getPageTitleStyle = (isMobile) => ({
  textAlign: 'center',
  color: '#333',
  marginBottom: '30px',
  fontSize: isMobile ? (window.innerWidth <= 480 ? '1.5em' : '2em') : '2.5em',
  fontWeight: 'bold',
});

const getPropertyCardLinkStyle = (isMobile) => ({
  textDecoration: 'none',
  color: 'inherit',
  flex: isMobile ? '1 1 100%' : '0 0 calc(33.333% - 14px)',
  maxWidth: isMobile ? '100%' : 'calc(33.333% - 14px)',
  boxSizing: 'border-box',
});

const getSearchInputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '10px 12px 10px 40px' : '12px 15px 12px 50px',
  borderRadius: '8px',
  border: '1px solid #ddd',
  fontSize: isMobile ? '1em' : '1.1em',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
});

const getSearchIconStyle = (isMobile) => ({
  position: 'absolute',
  left: isMobile ? '15px' : '20px',
  color: '#888',
  fontSize: isMobile ? '1.2em' : '1.3em',
});

// --- Original Static Styles ---

const propertyListingsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
  justifyContent: 'flex-start',
};

const propertyCardStyle = {
  backgroundColor: '#fff',
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  transition: 'transform 0.2s ease-in-out',
};

const propertyImageWrapperStyle = {
  position: 'relative',
  width: '100%',
  paddingBottom: '100%',
  overflow: 'hidden',
  backgroundColor: '#e0e0e0',
};

const propertyCardImageStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '10px 10px 0 0',
};

const propertyCardContentStyle = {
  padding: '15px',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
};

const propertyCardTitleStyle = {
  fontSize: '1.4em',
  margin: '0 0 5px 0',
  color: '#333',
  fontWeight: 'bold',
};

const propertyCardIdRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '5px',
};

const photoCountStyle = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.9em',
  color: '#666',
  fontWeight: 'normal',
};

const photoIconStyle = {
  marginRight: '4px',
  color: '#007bff',
  fontSize: '1.1em',
};

const propertyCardIdStyle = {
  fontSize: '0.9em',
  color: '#888',
  margin: '0',
  fontWeight: 'normal',
};

const propertyCardSubTitleStyle = {
  fontSize: '1em',
  margin: '0 0 10px 0',
  color: '#666',
};

const propertyInfoRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  marginBottom: '5px',
  alignItems: 'center',
};

const propertyLocationStyle = {
  fontSize: '0.95em',
  color: '#555',
  margin: '0',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  flexBasis: '100%',
};

const propertyDetailItemStyle = {
  fontSize: '0.95em',
  color: '#555',
  margin: '0',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  flex: '1 1 auto',
  minWidth: '30%',
};

const iconStyle = {
  marginRight: '5px',
  color: '#007bff',
  fontSize: '1.1em',
};

const propertyCardPriceContainerStyle = {
  marginTop: '15px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const propertyCardPriceLabelStyle = {
  fontSize: '1.0em',
  fontWeight: 'normal',
  color: '#000',
  marginBottom: '3px',
};

const propertyCardPriceValueStyle = {
  fontSize: '1.25em',
  fontWeight: 'bold',
  color: '#007bff',
};

const messageStyle = {
  textAlign: 'center',
  color: '#007bff',
  fontSize: '16px',
  marginTop: '20px',
};

const searchContainerStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  maxWidth: '500px',
  margin: '20px auto',
};

const errorStyle = {
  color: 'red',
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: '15px',
};

export default PropertyListings;